import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Divider,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Alert,
  AlertTitle,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Checkbox,
  Menu,
  CircularProgress,
  Tooltip,
  LinearProgress,
  Pagination
} from '@mui/material';
import {
  FileUpload as FileUploadIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Folder as FolderIcon,
  Description as DescriptionIcon,
  PictureAsPdf as PictureAsPdfIcon,
  TableChart as TableChartIcon,
  Slideshow as SlideshowIcon,
  Image as ImageIcon,
  InsertDriveFile as InsertDriveFileIcon,
  ExpandMore as ExpandMoreIcon,
  MoreVert as MoreVertIcon,
  MoveToInbox as MoveToInboxIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';

import documentsApi from '../../api/documentsApi';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorMessage from '../../components/common/ErrorMessage';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`documents-tabpanel-${index}`}
      aria-labelledby={`documents-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const DocumentsPage = () => {
  // 기본 상태
  const [documents, setDocuments] = useState([]);
  const [groups, setGroups] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [tabValue, setTabValue] = useState(0);

  // 문서 관련 상태
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [fileDescription, setFileDescription] = useState('');
  const [documentType, setDocumentType] = useState('unknown');
  const [selectedGroupForUpload, setSelectedGroupForUpload] = useState('');

  // 그룹 관련 상태
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [openGroupDialog, setOpenGroupDialog] = useState(false);
  const [openGroupDeleteDialog, setOpenGroupDeleteDialog] = useState(false);
  const [groupFormData, setGroupFormData] = useState({ name: '', description: '' });
  const [isGroupFormEdit, setIsGroupFormEdit] = useState(false);

  // 문서 이동 관련 상태
  const [moveMenuAnchor, setMoveMenuAnchor] = useState(null);
  const [documentToMove, setDocumentToMove] = useState(null);

  // 파일 업로드 관련 상태 추가
  const [uploadingFiles, setUploadingFiles] = useState([]);
  const [uploadResults, setUploadResults] = useState([]);

  // 문서 선택 관련 상태 추가
  const [selectedDocuments, setSelectedDocuments] = useState(new Set());
  const [openBulkDeleteDialog, setOpenBulkDeleteDialog] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);

  // 페이지네이션 관련 상태 추가
  const [currentPage, setCurrentPage] = useState(1);
  const [documentsPerPage] = useState(100);  // ✅ 10 → 100으로 변경 (대량 삭제 편의성)
  const [totalDocuments, setTotalDocuments] = useState(0);

  // 데이터 로딩
  const fetchDocuments = useCallback(async (page = currentPage) => {
    try {
      setIsLoading(true);
      const offset = (page - 1) * documentsPerPage;
      const response = await documentsApi.listDocuments({
        limit: documentsPerPage,
        offset: offset
      });
      setDocuments(response.documents || []);
      setTotalDocuments(response.total || 0);
    } catch (error) {
      console.error('문서 목록을 가져오는 중 오류 발생:', error);
      setError('문서 목록을 불러올 수 없습니다.');
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, documentsPerPage]);

  const fetchGroups = useCallback(async () => {
    try {
      const response = await documentsApi.listGroups();
      setGroups(response.groups || []);
    } catch (error) {
      console.error('그룹 목록을 가져오는 중 오류 발생:', error);
      setError('그룹 목록을 불러올 수 없습니다.');
    }
  }, []);

  useEffect(() => {
    fetchDocuments();
    fetchGroups();
  }, [fetchDocuments, fetchGroups]);

  // 문서 관련 핸들러
  const handleDelete = async () => {
    if (!selectedDocument) return;
    
    try {
      await documentsApi.deleteDocument(selectedDocument.document_id);
      setDocuments(prevDocuments => 
        prevDocuments.filter(doc => doc.document_id !== selectedDocument.document_id)
      );
      setOpenDeleteDialog(false);
      setSelectedDocument(null);
    } catch (error) {
      console.error('문서 삭제 중 오류 발생:', error);
      setError('문서를 삭제할 수 없습니다.');
    }
  };

  const handleMoveDocument = async (documentId, groupId) => {
    try {
      await documentsApi.updateDocumentGroup(documentId, groupId);
      fetchDocuments(); // 문서 목록 새로고침
      fetchGroups(); // 그룹의 문서 카운트 업데이트
      setMoveMenuAnchor(null);
      setDocumentToMove(null);
    } catch (error) {
      console.error('문서 이동 중 오류 발생:', error);
      setError('문서를 이동할 수 없습니다.');
    }
  };

  // 그룹 관련 핸들러
  const handleCreateGroup = async () => {
    try {
      await documentsApi.createGroup(groupFormData);
      fetchGroups();
      setOpenGroupDialog(false);
      setGroupFormData({ name: '', description: '' });
    } catch (error) {
      console.error('그룹 생성 중 오류 발생:', error);
      setError('그룹을 생성할 수 없습니다.');
    }
  };

  const handleUpdateGroup = async () => {
    try {
      await documentsApi.updateGroup(selectedGroup.group_id, groupFormData);
      fetchGroups();
      setOpenGroupDialog(false);
      setGroupFormData({ name: '', description: '' });
      setSelectedGroup(null);
    } catch (error) {
      console.error('그룹 수정 중 오류 발생:', error);
      setError('그룹을 수정할 수 없습니다.');
    }
  };

  const handleDeleteGroup = async () => {
    if (!selectedGroup) return;
    
    try {
      await documentsApi.deleteGroup(selectedGroup.group_id);
      fetchGroups();
      fetchDocuments(); // 문서 목록도 새로고침 (그룹 정보 업데이트)
      setOpenGroupDeleteDialog(false);
      setSelectedGroup(null);
    } catch (error) {
      console.error('그룹 삭제 중 오류 발생:', error);
      setError('그룹을 삭제할 수 없습니다.');
    }
  };

  const openEditGroupDialog = (group) => {
    setSelectedGroup(group);
    setGroupFormData({ name: group.name, description: group.description || '' });
    setIsGroupFormEdit(true);
    setOpenGroupDialog(true);
  };

  const openCreateGroupDialog = () => {
    setSelectedGroup(null);
    setGroupFormData({ name: '', description: '' });
    setIsGroupFormEdit(false);
    setOpenGroupDialog(true);
  };

  // 파일 업로드 관련
  const getFileIcon = (mimeType) => {
    if (mimeType && mimeType.startsWith('image/')) {
      return <ImageIcon />;
    } else if (mimeType && mimeType.includes('pdf')) {
      return <PictureAsPdfIcon />;
    } else if (mimeType && (mimeType.includes('spreadsheet') || mimeType.includes('excel'))) {
      return <TableChartIcon />;
    } else if (mimeType && (mimeType.includes('presentation') || mimeType.includes('powerpoint'))) {
      return <SlideshowIcon />;
    } else if (mimeType && (mimeType.includes('word') || mimeType.includes('document'))) {
      return <DescriptionIcon />;
    } else {
      return <InsertDriveFileIcon />;
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;
    
    setIsUploading(true);
    setUploadError(null);
    setUploadResults([]); // 이전 결과 초기화
    
    // 업로드할 파일들의 상태 초기화
    const fileStates = acceptedFiles.map((file, index) => ({
      id: `file-${Date.now()}-${index}`,
      file,
      name: file.name,
      status: 'waiting', // waiting, uploading, success, failed
      progress: 0,
      error: null,
      document_id: null
    }));
    
    setUploadingFiles(fileStates);
    
    const results = [];
    
    // 파일들을 순차적으로 업로드 (동시 업로드하면 서버 부하가 클 수 있음)
    for (let i = 0; i < fileStates.length; i++) {
      const fileState = fileStates[i];
      
      try {
        // 현재 파일을 업로딩 상태로 변경
        setUploadingFiles(prev => 
          prev.map(f => f.id === fileState.id ? { ...f, status: 'uploading', progress: 0 } : f)
        );
        
        const formData = new FormData();
        formData.append('file', fileState.file);
        
        // 자동 감지가 아닌 경우에만 document_type 추가
        if (documentType !== 'unknown') {
          formData.append('document_type', documentType);
        }
        
        if (fileDescription.trim()) {
          formData.append('description', fileDescription);
        }
        
        if (selectedGroupForUpload) {
          formData.append('group_id', selectedGroupForUpload);
        }
        
        const response = await documentsApi.uploadDocument(formData);
        
        // 성공 상태로 업데이트
        setUploadingFiles(prev => 
          prev.map(f => f.id === fileState.id ? { 
            ...f, 
            status: 'success', 
            progress: 100,
            document_id: response.document_id 
          } : f)
        );
        
        results.push({
          fileName: fileState.name,
          status: 'success',
          document_id: response.document_id
        });
        
      } catch (error) {
        console.error(`파일 ${fileState.name} 업로드 중 오류:`, error);
        
        // 실패 상태로 업데이트
        setUploadingFiles(prev => 
          prev.map(f => f.id === fileState.id ? { 
            ...f, 
            status: 'failed', 
            error: error.response?.data?.detail || error.message || '업로드 실패'
          } : f)
        );
        
        results.push({
          fileName: fileState.name,
          status: 'failed',
          error: error.response?.data?.detail || error.message || '업로드 실패'
        });
      }
    }
    
    setUploadResults(results);
    setIsUploading(false);
    
    // 성공한 파일이 있으면 목록 새로고침
    const successCount = results.filter(r => r.status === 'success').length;
    if (successCount > 0) {
      fetchDocuments();
      fetchGroups();
    }
    
    // 모든 파일이 성공하면 폼 초기화
    if (successCount === results.length) {
      setFileDescription('');
      setDocumentType('unknown');
      setSelectedGroupForUpload('');
      // 3초 후 업로드 상태 초기화
      setTimeout(() => {
        setUploadingFiles([]);
        setUploadResults([]);
      }, 3000);
    }
  }, [fileDescription, documentType, selectedGroupForUpload, fetchDocuments, fetchGroups]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
      'application/vnd.ms-powerpoint': ['.ppt'],
      'application/haansofthwp': ['.hwp'],
      'application/x-hwp': ['.hwp'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'text/plain': ['.txt'],
      'text/markdown': ['.md', '.markdown'],
      'text/csv': ['.csv'],
    },
    disabled: isUploading,
  });

  // 그룹별 문서 분류
  const groupedDocuments = documents.reduce((acc, doc) => {
    const groupId = doc.group_id || 'ungrouped';
    if (!acc[groupId]) {
      acc[groupId] = [];
    }
    acc[groupId].push(doc);
    return acc;
  }, {});

  // 문서 선택 관련 핸들러
  const handleSelectDocument = (documentId, checked) => {
    const newSelected = new Set(selectedDocuments);
    if (checked) {
      newSelected.add(documentId);
    } else {
      newSelected.delete(documentId);
    }
    setSelectedDocuments(newSelected);
  };

  const handleSelectAll = (documents, checked) => {
    const newSelected = new Set(selectedDocuments);
    if (checked) {
      documents.forEach(doc => newSelected.add(doc.document_id));
    } else {
      documents.forEach(doc => newSelected.delete(doc.document_id));
    }
    setSelectedDocuments(newSelected);
  };

  // 현재 페이지의 모든 문서 선택/해제
  const handleSelectAllInPage = (checked) => {
    const newSelected = new Set(selectedDocuments);
    if (checked) {
      documents.forEach(doc => newSelected.add(doc.document_id));
    } else {
      documents.forEach(doc => newSelected.delete(doc.document_id));
    }
    setSelectedDocuments(newSelected);
  };

  // 현재 페이지의 모든 문서가 선택되었는지 확인
  const allPageDocsSelected = documents.length > 0 && documents.every(doc => selectedDocuments.has(doc.document_id));
  const somePageDocsSelected = documents.some(doc => selectedDocuments.has(doc.document_id));

  const handleBulkDelete = async () => {
    if (selectedDocuments.size === 0) return;
    
    try {
      setBulkDeleteLoading(true);
      const documentIds = Array.from(selectedDocuments);
      const result = await documentsApi.bulkDeleteDocuments(documentIds);
      
      // 선택 상태 초기화
      setSelectedDocuments(new Set());
      setOpenBulkDeleteDialog(false);
      
      // 결과 알림
      if (result.failed_count > 0) {
        setError(`${result.deleted_count}개 문서 삭제 완료, ${result.failed_count}개 문서 삭제 실패`);
      } else {
        // 성공 메시지 표시
        setSuccessMessage(`${result.deleted_count}개 문서가 성공적으로 삭제되었습니다.`);
        setTimeout(() => setSuccessMessage(null), 3000);
      }
      
      // 문서 목록 새로고침 (현재 페이지 유지)
      fetchDocuments(currentPage);
      // 그룹 카운트 업데이트
      fetchGroups();
      
    } catch (error) {
      console.error('일괄 삭제 중 오류 발생:', error);
      setError('문서를 삭제할 수 없습니다.');
    } finally {
      setBulkDeleteLoading(false);
    }
  };

  // 페이지네이션 핸들러
  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
    setSelectedDocuments(new Set()); // 페이지 변경 시 선택 초기화
    fetchDocuments(newPage);
  };

  const totalPages = Math.ceil(totalDocuments / documentsPerPage);

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        문서 관리
      </Typography>
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab label="문서 업로드" />
          <Tab label="문서 목록" />
          <Tab label="그룹 관리" />
        </Tabs>
      </Box>

      {/* 문서 업로드 탭 */}
      <TabPanel value={tabValue} index={0}>
      <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              문서 업로드
            </Typography>
            <Divider sx={{ my: 2 }} />
            
            <Box sx={{ mb: 3 }}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>문서 유형</InputLabel>
                <Select
                  value={documentType}
                  label="문서 유형"
                  onChange={(e) => setDocumentType(e.target.value)}
                >
                  <MenuItem value="unknown">자동 감지</MenuItem>
                  <MenuItem value="pdf">PDF</MenuItem>
                  <MenuItem value="word">Word 문서</MenuItem>
                  <MenuItem value="excel">Excel 스프레드시트</MenuItem>
                  <MenuItem value="powerpoint">PowerPoint 프레젠테이션</MenuItem>
                  <MenuItem value="hwp">HWP 문서</MenuItem>
                  <MenuItem value="hwpx">HWPX 문서</MenuItem>
                  <MenuItem value="image">이미지</MenuItem>
                  <MenuItem value="text">텍스트/마크다운</MenuItem>
                </Select>
              </FormControl>

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>문서 그룹 (선택사항)</InputLabel>
                  <Select
                    value={selectedGroupForUpload}
                    label="문서 그룹 (선택사항)"
                    onChange={(e) => setSelectedGroupForUpload(e.target.value)}
                  >
                    <MenuItem value="">그룹 없음</MenuItem>
                    {groups.map((group) => (
                      <MenuItem key={group.group_id} value={group.group_id}>
                        {group.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
              
              <TextField
                fullWidth
                label="문서 설명 (선택사항)"
                variant="outlined"
                value={fileDescription}
                onChange={(e) => setFileDescription(e.target.value)}
                sx={{ mb: 2 }}
              />
            </Box>
            
            <Box
              {...getRootProps()}
              sx={{
                border: '2px dashed #ccc',
                borderRadius: 2,
                p: 3,
                textAlign: 'center',
                cursor: 'pointer',
                backgroundColor: isDragActive ? 'rgba(0, 0, 0, 0.05)' : 'transparent',
                transition: 'background-color 0.2s ease-in-out',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.05)'
                }
              }}
            >
              <input {...getInputProps()} />
              {isUploading ? (
                <LoadingSpinner message="파일 업로드 중..." />
              ) : (
                <>
                  <FileUploadIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                  <Typography>
                    {isDragActive
                      ? '파일들을 여기에 놓으세요...'
                      : '클릭하거나 파일들을 끌어다 놓으세요'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    지원 파일: PDF, Word, Excel, PowerPoint, HWP, HWPX, JPG, PNG, TXT, MD, CSV
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    여러 파일을 동시에 업로드할 수 있습니다
                  </Typography>
                </>
              )}
            </Box>
            
            {uploadError && (
              <Box sx={{ mt: 2 }}>
                <ErrorMessage message={uploadError} />
              </Box>
            )}
            
            {/* 업로드 진행 상황 표시 */}
            {uploadingFiles.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom>
                  업로드 진행 상황
                </Typography>
                <List dense>
                  {uploadingFiles.map((file) => (
                    <ListItem key={file.id}>
                      <ListItemIcon>
                        {file.status === 'success' && <CheckCircleIcon color="success" />}
                        {file.status === 'failed' && <DeleteIcon color="error" />}
                        {file.status === 'uploading' && <CircularProgress size={20} />}
                        {file.status === 'waiting' && <DescriptionIcon color="action" />}
                      </ListItemIcon>
                      <ListItemText
                        primary={file.name}
                        secondary={
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              {file.status === 'waiting' && '대기 중...'}
                              {file.status === 'uploading' && '업로드 중...'}
                              {file.status === 'success' && '업로드 완료'}
                              {file.status === 'failed' && `실패: ${file.error}`}
                            </Typography>
                            {file.status === 'uploading' && (
                              <Box sx={{ width: '100%', mt: 1 }}>
                                <LinearProgress />
                              </Box>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
            
            {/* 업로드 결과 요약 */}
            {uploadResults.length > 0 && !isUploading && (
              <Box sx={{ mt: 2 }}>
                <Alert 
                  severity={
                    uploadResults.every(r => r.status === 'success') ? 'success' : 
                    uploadResults.every(r => r.status === 'failed') ? 'error' : 'warning'
                  }
                >
                  <AlertTitle>업로드 완료</AlertTitle>
                  성공: {uploadResults.filter(r => r.status === 'success').length}개, 
                  실패: {uploadResults.filter(r => r.status === 'failed').length}개
                  {uploadResults.some(r => r.status === 'failed') && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2">
                        실패한 파일들: {uploadResults.filter(r => r.status === 'failed').map(r => r.fileName).join(', ')}
                      </Typography>
                    </Box>
                  )}
                </Alert>
              </Box>
            )}
          </Paper>
        </Grid>
        </Grid>
      </TabPanel>
        
      {/* 문서 목록 탭 */}
      <TabPanel value={tabValue} index={1}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6">
                  문서 목록
                </Typography>
                {documents.length > 0 && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={allPageDocsSelected}
                        indeterminate={somePageDocsSelected && !allPageDocsSelected}
                        onChange={(e) => handleSelectAllInPage(e.target.checked)}
                      />
                    }
                    label="페이지 전체 선택"
                    sx={{ ml: 2 }}
                  />
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                {selectedDocuments.size > 0 && (
                  <>
                    <Typography variant="body2" color="primary">
                      {selectedDocuments.size}개 선택됨
                    </Typography>
                    <Button
                      startIcon={<DeleteIcon />}
                      variant="contained"
                      color="error"
                      size="small"
                      onClick={() => setOpenBulkDeleteDialog(true)}
                    >
                      선택 삭제
                    </Button>
                  </>
                )}
                <Button
                  startIcon={<RefreshIcon />}
                  onClick={() => fetchDocuments(currentPage)}
                  disabled={isLoading}
                >
                  새로고침
                </Button>
              </Box>
            </Box>
            <Divider sx={{ mb: 2 }} />
            
            {isLoading ? (
              <LoadingSpinner message="문서 목록을 불러오는 중..." />
            ) : error ? (
              <ErrorMessage message={error} />
            ) : successMessage ? (
              <Alert severity="success" sx={{ mb: 2 }}>
                {successMessage}
              </Alert>
            ) : documents.length === 0 ? (
              <Alert severity="info">
                <AlertTitle>문서 없음</AlertTitle>
                  아직 업로드된 문서가 없습니다. 업로드 탭에서 문서를 업로드하세요.
              </Alert>
            ) : (
                <>
                  {/* 그룹별 문서 표시 */}
                  {groups.map((group) => {
                    const groupDocs = groupedDocuments[group.group_id] || [];
                    if (groupDocs.length === 0) return null;
                    const allGroupDocsSelected = groupDocs.every(doc => selectedDocuments.has(doc.document_id));
                    const someGroupDocsSelected = groupDocs.some(doc => selectedDocuments.has(doc.document_id));

                    return (
                      <Accordion key={group.group_id} defaultExpanded>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                            <Checkbox
                              checked={allGroupDocsSelected}
                              indeterminate={someGroupDocsSelected && !allGroupDocsSelected}
                              onChange={(e) => handleSelectAll(groupDocs, e.target.checked)}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <FolderIcon color="primary" />
                            <Typography variant="subtitle1" fontWeight="bold">
                              {group.name}
                            </Typography>
                            <Chip label={`${groupDocs.length}개`} size="small" />
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          <List dense>
                            {groupDocs.map((document) => (
                              <ListItem key={document.document_id}>
                                <ListItemIcon>
                                  <Checkbox
                                    checked={selectedDocuments.has(document.document_id)}
                                    onChange={(e) => handleSelectDocument(document.document_id, e.target.checked)}
                                  />
                                </ListItemIcon>
                                <ListItemIcon>
                                  {getFileIcon(document.mime_type)}
                                </ListItemIcon>
                                <ListItemText
                                  primary={document.filename}
                                  secondary={
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                      <Typography variant="body2" color="text.secondary">
                                        {document.description || '설명 없음'}
                                      </Typography>
                                      <Box>
                                        <Chip 
                                          label={formatFileSize(document.file_size)} 
                                          size="small" 
                                          sx={{ mr: 1, mb: 0.5 }}
                                        />
                                        <Chip 
                                          label={document.document_type} 
                                          size="small" 
                                          color="primary" 
                                          variant="outlined"
                                          sx={{ mr: 1, mb: 0.5 }}
                                        />
                                        <Chip 
                                          label={document.status} 
                                          size="small" 
                                          color={
                                            document.status === 'processed' 
                                              ? 'success' 
                                              : document.status === 'processing' 
                                                ? 'warning' 
                                                : 'default'
                                          }
                                          sx={{ mb: 0.5 }}
                                        />
                                      </Box>
                                    </Box>
                                  }
                                />
                                <ListItemSecondaryAction>
                                  <Tooltip title="문서 이동">
                                    <IconButton
                                      onClick={(e) => {
                                        setMoveMenuAnchor(e.currentTarget);
                                        setDocumentToMove(document);
                                      }}
                                    >
                                      <MoveToInboxIcon />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="삭제">
                                    <IconButton
                                      onClick={() => {
                                        setSelectedDocument(document);
                                        setOpenDeleteDialog(true);
                                      }}
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </Tooltip>
                                </ListItemSecondaryAction>
                              </ListItem>
                            ))}
                          </List>
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}

                  {/* 그룹에 속하지 않은 문서들 */}
                  {groupedDocuments.ungrouped && groupedDocuments.ungrouped.length > 0 && (
                    <Accordion defaultExpanded>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                          <Checkbox
                            checked={groupedDocuments.ungrouped.every(doc => selectedDocuments.has(doc.document_id))}
                            indeterminate={
                              groupedDocuments.ungrouped.some(doc => selectedDocuments.has(doc.document_id)) &&
                              !groupedDocuments.ungrouped.every(doc => selectedDocuments.has(doc.document_id))
                            }
                            onChange={(e) => handleSelectAll(groupedDocuments.ungrouped, e.target.checked)}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <DescriptionIcon color="action" />
                          <Typography variant="subtitle1" fontWeight="bold">
                            그룹 없음
                          </Typography>
                          <Chip label={`${groupedDocuments.ungrouped.length}개`} size="small" />
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <List dense>
                          {groupedDocuments.ungrouped.map((document) => (
                            <ListItem key={document.document_id}>
                              <ListItemIcon>
                                <Checkbox
                                  checked={selectedDocuments.has(document.document_id)}
                                  onChange={(e) => handleSelectDocument(document.document_id, e.target.checked)}
                                />
                              </ListItemIcon>
                              <ListItemIcon>
                                {getFileIcon(document.mime_type)}
                              </ListItemIcon>
                              <ListItemText
                                primary={document.filename}
                                secondary={
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Typography variant="body2" color="text.secondary">
                                      {document.description || '설명 없음'}
                                    </Typography>
                                    <Box>
                                      <Chip 
                                        label={formatFileSize(document.file_size)} 
                                        size="small" 
                                        sx={{ mr: 1, mb: 0.5 }}
                                      />
                                      <Chip 
                                        label={document.document_type} 
                                        size="small" 
                                        color="primary" 
                                        variant="outlined"
                                        sx={{ mr: 1, mb: 0.5 }}
                                      />
                                      <Chip 
                                        label={document.status} 
                                        size="small" 
                                        color={
                                          document.status === 'processed' 
                                            ? 'success' 
                                            : document.status === 'processing' 
                                              ? 'warning' 
                                              : 'default'
                                        }
                                        sx={{ mb: 0.5 }}
                                      />
                                    </Box>
                                  </Box>
                                }
                              />
                              <ListItemSecondaryAction>
                                <Tooltip title="문서 이동">
                                  <IconButton
                                    onClick={(e) => {
                                      setMoveMenuAnchor(e.currentTarget);
                                      setDocumentToMove(document);
                                    }}
                                  >
                                    <MoveToInboxIcon />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="삭제">
                                  <IconButton
                                    onClick={() => {
                                      setSelectedDocument(document);
                                      setOpenDeleteDialog(true);
                                    }}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip>
                              </ListItemSecondaryAction>
                            </ListItem>
                          ))}
                        </List>
                      </AccordionDetails>
                    </Accordion>
                  )}
                </>
              )}
              
              {/* 페이지네이션 */}
              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    size="large"
                    showFirstButton
                    showLastButton
                  />
                </Box>
              )}
              
              {/* 페이지 정보 표시 */}
              {totalDocuments > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    전체 {totalDocuments}개 문서 중 {((currentPage - 1) * documentsPerPage) + 1}-{Math.min(currentPage * documentsPerPage, totalDocuments)}개 표시
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </TabPanel>

      {/* 그룹 관리 탭 */}
      <TabPanel value={tabValue} index={2}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper elevation={3} sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  문서 그룹 관리
                </Typography>
                <Button
                  startIcon={<AddIcon />}
                  variant="contained"
                  onClick={openCreateGroupDialog}
                >
                  새 그룹 생성
                </Button>
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              {groups.length === 0 ? (
                <Alert severity="info">
                  <AlertTitle>그룹 없음</AlertTitle>
                  아직 생성된 그룹이 없습니다. 새 그룹을 생성해보세요.
                </Alert>
              ) : (
                <List>
                  {groups.map((group) => (
                    <ListItem key={group.group_id}>
                      <ListItemIcon>
                        <FolderIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={group.name}
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              {group.description || '설명 없음'}
                            </Typography>
                            <Chip 
                              label={`${group.document_count}개 문서`} 
                              size="small" 
                              sx={{ mt: 0.5 }}
                            />
                          </Box>
                        }
                      />
                      <ListItemSecondaryAction>
                        <IconButton onClick={() => openEditGroupDialog(group)}>
                          <EditIcon />
                        </IconButton>
                        <IconButton 
                          onClick={() => {
                            setSelectedGroup(group);
                            setOpenGroupDeleteDialog(true);
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </Grid>
      </Grid>
      </TabPanel>
      
      {/* 문서 삭제 확인 다이얼로그 */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitle>문서 삭제</DialogTitle>
        <DialogContent>
          <DialogContentText>
            "{selectedDocument?.filename}"을(를) 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>취소</Button>
          <Button onClick={handleDelete} color="error">삭제</Button>
        </DialogActions>
      </Dialog>

      {/* 그룹 생성/수정 다이얼로그 */}
      <Dialog
        open={openGroupDialog}
        onClose={() => setOpenGroupDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {isGroupFormEdit ? '그룹 수정' : '새 그룹 생성'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="그룹 이름"
            fullWidth
            variant="outlined"
            value={groupFormData.name}
            onChange={(e) => setGroupFormData({ ...groupFormData, name: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="그룹 설명 (선택사항)"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={groupFormData.description}
            onChange={(e) => setGroupFormData({ ...groupFormData, description: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenGroupDialog(false)}>취소</Button>
          <Button 
            onClick={isGroupFormEdit ? handleUpdateGroup : handleCreateGroup}
            variant="contained"
            disabled={!groupFormData.name.trim()}
          >
            {isGroupFormEdit ? '수정' : '생성'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 그룹 삭제 확인 다이얼로그 */}
      <Dialog
        open={openGroupDeleteDialog}
        onClose={() => setOpenGroupDeleteDialog(false)}
      >
        <DialogTitle>그룹 삭제</DialogTitle>
        <DialogContent>
          <DialogContentText>
            "{selectedGroup?.name}" 그룹을 삭제하시겠습니까? 
            그룹에 속한 문서들은 "그룹 없음"으로 이동됩니다.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenGroupDeleteDialog(false)}>취소</Button>
          <Button onClick={handleDeleteGroup} color="error">삭제</Button>
        </DialogActions>
      </Dialog>

      {/* 일괄 삭제 확인 다이얼로그 */}
      <Dialog
        open={openBulkDeleteDialog}
        onClose={() => setOpenBulkDeleteDialog(false)}
      >
        <DialogTitle>일괄 삭제</DialogTitle>
        <DialogContent>
          <DialogContentText>
            선택한 {selectedDocuments.size}개의 문서를 삭제하시겠습니까? 
            이 작업은 되돌릴 수 없습니다.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setOpenBulkDeleteDialog(false)}
            disabled={bulkDeleteLoading}
          >
            취소
          </Button>
          <Button 
            onClick={handleBulkDelete} 
            color="error"
            disabled={bulkDeleteLoading}
            startIcon={bulkDeleteLoading ? <CircularProgress size={20} /> : null}
          >
            {bulkDeleteLoading ? '삭제 중...' : '삭제'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 문서 이동 메뉴 */}
      <Menu
        anchorEl={moveMenuAnchor}
        open={Boolean(moveMenuAnchor)}
        onClose={() => setMoveMenuAnchor(null)}
      >
        <MenuItem 
          onClick={() => handleMoveDocument(documentToMove?.document_id, null)}
        >
          그룹 없음으로 이동
        </MenuItem>
        {groups.map((group) => (
          <MenuItem 
            key={group.group_id}
            onClick={() => handleMoveDocument(documentToMove?.document_id, group.group_id)}
          >
            {group.name}로 이동
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default DocumentsPage; 